import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Table from "components/DesignSystem/Table/V2/Table";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { PlusIcon } from "components/icons/PlusIcon";
import { FilterAndSort } from "components/icons/FilterSort";
import { DISPLAY_DATE } from "constants/date";
import {
  FIXED_TRANSACTION_COLUMNS,
  TRANSACTION_COLUMNS,
  TRANSACTION_COLUMNS_OPTIONS,
} from "constants/transaction";
import classNames from "classnames";
import dayjs from "dayjs";
import { ChangeEvent, Fragment } from "react";
import { Row, createColumnHelper, flexRender } from "react-table-8.10.7";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { TransactionAnimateRow } from "./TransactionAnimateRow";
import { TransactionDraggableRow } from "./TransactionDraggableRow";
import {
  TXN_ACCOUNT,
  type Transaction,
  type Transactions as TransactionsType,
  type TxnAccountType,
} from "types/Models/books";
import { currency } from "utils/Currency";
import { AddTransactionComment } from "./AddTransactionComment";
import { AddTransactionInvoice } from "./AddTransactionInvoice";
import { AddTransactionMemo } from "./AddTransactionMemo";
import { SuggestedInvoiceBillColumn } from "./SuggestedInvoiceBillColumn";
import TransactionColumn from "./TransactionColumn";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { TxnClassificationAssignment } from "components/ClassificationAssignment/TxnClassificationAssignment";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { ShiftRowSelectHandler } from "hooks/useShiftRowSelection";

const ClassificationHeaderCell = () => {
  const entityId = useCurrentEntityId();
  const permission = useHasSubscriptionPermission({
    feature: CLASSIFICATION_FEATURE_ACCESS,
    product_type: "BOOKS",
    parentEntityId: entityId,
  });

  return (
    <div className="t-flex t-items-center t-gap-1">
      <HeadCellContent title="Classification" />
      {permission?.blocked && (
        <div className="!t-text-purple-55">
          <DiamondPurple height={12} width={12} />
        </div>
      )}
    </div>
  );
};

export type TransactionTableColumns = {
  COLUMNS: (keyof typeof TRANSACTION_COLUMNS)[];
};

export const TransactionTableHandler = ({
  values,
  updateFilter,
  txnAccountType,
}: {
  values: TransactionTableColumns;
  updateFilter: <S extends keyof TransactionTableColumns>(
    name: S,
    newValue: TransactionTableColumns[S]
  ) => void;
  txnAccountType?: TxnAccountType;
}) => {
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;

    if (checked) {
      updateFilter("COLUMNS", [
        ...values.COLUMNS,
        name as keyof typeof TRANSACTION_COLUMNS,
      ]);
      return;
    }

    updateFilter(
      "COLUMNS",
      values.COLUMNS.filter((column) => column !== name)
    );
  };

  const filteredOptions =
    txnAccountType === TXN_ACCOUNT.STRIPE
      ? TRANSACTION_COLUMNS_OPTIONS.filter(
          (option) => option.value !== TRANSACTION_COLUMNS.CLASSIFICATION
        )
      : TRANSACTION_COLUMNS_OPTIONS.filter(
          (option) => option.value !== TRANSACTION_COLUMNS.DAILY_ENTRY
        );

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <span>
          <ToolTip text="Add columns" side="bottom">
            <Button customType="ghost" size="xsmall">
              <PlusIcon size="12" />
            </Button>
          </ToolTip>
        </span>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content>
          {filteredOptions.map((option) => {
            const { label, value } = option;

            return (
              <Dropdown.Item key={value} onSelect={(e) => e.preventDefault()}>
                <Checkbox
                  disabled={FIXED_TRANSACTION_COLUMNS.includes(value)}
                  label={
                    <div className="t-lowercase first-letter:t-uppercase">
                      {label}
                    </div>
                  }
                  name={value}
                  checked={values.COLUMNS.includes(value)}
                  onChange={onChange}
                />
              </Dropdown.Item>
            );
          })}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export const txnColumnHelper = createColumnHelper<TransactionsType>();

export const transactionColumn = {
  select: (
    allSelectedRows: string,
    onRowSelect?: ShiftRowSelectHandler<TransactionsType>
  ) =>
    txnColumnHelper.display({
      id: TRANSACTION_COLUMNS.SELECT,
      size: 20,
      enableSorting: false,
      header: ({ table }) => <SelectionHeaderCell table={table} />,
      cell: ({ row, table }) => (
        <SelectionCell
          row={row}
          onChange={
            onRowSelect
              ? (e) =>
                  onRowSelect({
                    row,
                    table,
                    isShiftKey:
                      e.nativeEvent instanceof MouseEvent &&
                      e.nativeEvent.shiftKey,
                  })
              : undefined
          }
          visibility="hover"
          hasSelection={!!allSelectedRows}
          ariaLabel={`Select transaction, source: ${
            row.original.transaction.merchant || "Unassingned"
          }, amount: ${currency({
            amount: Number(row.original.transaction.amount || 0),
          })}, and description: ${
            row.original.transaction.description || "Empty"
          }`}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    }),

  date: ({ size = 120 }: { size?: number } = {}) =>
    txnColumnHelper.accessor("transaction.date", {
      id: TRANSACTION_COLUMNS.DATE,
      size,
      header: (header) => (
        <HeadCellContent
          title="Date"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <div className="t-text-body-sm t-text-text-30">
          {dayjs(info.getValue()).format(DISPLAY_DATE)}
        </div>
      ),
    }),

  from: ({ size = 71 }: { size?: number } = {}) =>
    txnColumnHelper.accessor("transaction.from", {
      id: TRANSACTION_COLUMNS.FROM,
      size,
      enableSorting: false,
      header: () => <HeadCellContent title="Source" align="center" />,
      cell: (info) => {
        const transaction = info.row.original.transaction;

        return <TransactionColumn.Source transaction={transaction} />;
      },
    }),

  merchant: ({
    size = 181,
    disabled,
  }: { size?: number; disabled?: boolean } = {}) =>
    txnColumnHelper.accessor("transaction.merchant", {
      id: TRANSACTION_COLUMNS.VENDOR,
      size,
      header: (header) => (
        <HeadCellContent
          title="To/From"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        return (
          <div>
            <TransactionColumn.Merchant
              info={info}
              showSplitVendors
              disabled={disabled}
            />
          </div>
        );
      },
    }),

  description: ({ size = 240 }: { size?: number } = {}) =>
    txnColumnHelper.accessor("transaction.description", {
      id: TRANSACTION_COLUMNS.DESCRIPTION,
      size,
      header: (header) => (
        <HeadCellContent
          title="Description"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const description = info.getValue();
        return (
          <ConditionalToolTip
            condition={
              <div className="t-max-w-60 t-word-break">{description}</div>
            }
          >
            <span className="t-line-clamp-1 t-break-all t-text-body-sm t-text-text-60">
              {description || "-"}
            </span>
          </ConditionalToolTip>
        );
      },
    }),

  amount: ({ size = 141 }: { size?: number } = {}) =>
    txnColumnHelper.accessor("transaction.amount", {
      id: TRANSACTION_COLUMNS.AMOUNT,
      size,
      header: (header) => {
        const meta = header.table.options.meta as
          | {
              displayCurrency: string;
              setDisplayCurrency: (v: string) => void;
            }
          | undefined;

        const filter = meta?.displayCurrency !== undefined && (
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <Button customType="ghost_icon" size="xsmall">
                <FilterAndSort />
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content
                sideOffset={8}
                side="bottom"
                className="t-min-w-44"
                align="end"
              >
                <Dropdown.RadioGroup
                  value={meta.displayCurrency}
                  onValueChange={meta.setDisplayCurrency}
                >
                  <Dropdown.RadioItem value="USD">In USD</Dropdown.RadioItem>
                  <Dropdown.RadioItem value="TXN_CURRENCY">
                    In txn currency
                  </Dropdown.RadioItem>
                </Dropdown.RadioGroup>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        );

        return (
          <HeadCellContent
            title="Amount"
            align="right"
            sort={{
              nextSortOrder: header.column.getNextSortingOrder(),
              isSorted: header.column.getIsSorted(),
              sortingHandler: header.column.getToggleSortingHandler(),
            }}
            filter={filter || undefined}
          />
        );
      },
      cell: (info) => {
        const usdAmount = info.getValue();
        const { transaction } = info.row.original;
        const { original_amount, currency } = transaction;

        const meta = info.table.options.meta as
          | { displayCurrency: string }
          | undefined;

        const showOriginal = meta?.displayCurrency === "TXN_CURRENCY";

        const displayAmount = showOriginal
          ? Number(original_amount) || usdAmount
          : usdAmount;
        const displayCurrency = showOriginal ? currency || "USD" : undefined;

        return (
          <TransactionColumn.Amount
            amount={displayAmount}
            currencyCode={displayCurrency}
          />
        );
      },
    }),

  category: ({
    size = 225,
    isDisabled = false,
  }: { size?: number; isDisabled?: boolean } = {}) =>
    txnColumnHelper.accessor("transaction.category", {
      id: TRANSACTION_COLUMNS.CATEGORY,
      size,
      header: () => <HeadCellContent title="Category" />,
      enableSorting: false,
      cell: (info) => {
        return (
          <div>
            <TransactionColumn.Category
              info={info}
              tooltip
              truncate
              showSplitActions
              isDisabled={isDisabled}
            />
          </div>
        );
      },
    }),

  classification: ({
    size = 240,
    isDisabled = false,
  }: { size?: number; isDisabled?: boolean } = {}) =>
    txnColumnHelper.accessor("has_classes", {
      id: TRANSACTION_COLUMNS.CLASSIFICATION,
      size,
      header: () => <ClassificationHeaderCell />,
      enableSorting: false,
      cell: (info) => {
        const transactionId = info.row.original.transaction.uuid;
        const hasClasses = info.getValue();
        const hasSuggestions = info.row.original.has_suggestions ?? false;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <TxnClassificationAssignment
              transactionId={transactionId}
              has_classes={hasClasses}
              has_suggestions={hasSuggestions}
            />
          </div>
        );
      },
    }),

  daily_entry: ({ size = 150 }: { size?: number } = {}) =>
    txnColumnHelper.accessor("stripe_aggregate_entry_ledger_entry", {
      id: TRANSACTION_COLUMNS.DAILY_ENTRY,
      size,
      header: () => <HeadCellContent title="Daily Entry" />,
      enableSorting: false,
      cell: (info) => {
        return <TransactionColumn.DailyEntry transaction={info.row.original} />;
      },
    }),

  invoice: ({
    size = 50,
    hasWritePermission,
  }: {
    size?: number;
    hasWritePermission: boolean;
  }) =>
    txnColumnHelper.accessor("invoices", {
      id: TRANSACTION_COLUMNS.INVOICE,
      size,
      header: () => <HeadCellContent title="" />,
      enableSorting: false,
      cell: ({ row }) => (
        <AddTransactionInvoice
          transactionDetails={row.original}
          hasWritePermission={hasWritePermission}
        />
      ),
    }),

  suggested: ({
    size = 50,
    hasWritePermission,
  }: {
    size?: number;
    hasWritePermission: boolean;
  }) =>
    txnColumnHelper.accessor("invoices", {
      id: TRANSACTION_COLUMNS.SUGGESTED,
      size,
      header: () => <HeadCellContent title="" />,
      enableSorting: false,
      cell: ({ row }) => (
        <SuggestedInvoiceBillColumn
          transactionDetails={row.original}
          hasWritePermission={hasWritePermission}
        />
      ),
    }),

  memo: ({
    size = 50,
    hasWritePermission,
  }: {
    size?: number;
    hasWritePermission: boolean;
  }) =>
    txnColumnHelper.accessor("transaction.memo", {
      id: TRANSACTION_COLUMNS.MEMO,
      size,
      header: () => <HeadCellContent title="" />,
      enableSorting: false,
      cell: ({ row }) => (
        <AddTransactionMemo
          transactionDetails={row.original}
          hasWritePermission={hasWritePermission}
        />
      ),
    }),

  comments: ({
    size = 50,
    hasWritePermission,
  }: {
    size?: number;
    hasWritePermission: boolean;
  }) =>
    txnColumnHelper.accessor("comments_count", {
      id: TRANSACTION_COLUMNS.COMMENTS,
      size,
      header: () => <HeadCellContent title="" />,
      enableSorting: false,
      cell: ({ row }) => (
        <AddTransactionComment
          transactionDetails={row.original}
          hasWritePermission={hasWritePermission}
        />
      ),
    }),

  options: ({
    onRequestInfo,
    sendToChat,
    onRowClick,
    onDuplicate,
    size = 43,
  }: {
    onRequestInfo: ({ transactionId }: { transactionId: string }) => void;
    sendToChat: ({ transactionIds }: { transactionIds: string[] }) => void;
    onRowClick: (uuid: string, e: Event) => void;
    onDuplicate: (transaction: Transaction) => void;
    size?: number;
  }) =>
    txnColumnHelper.accessor("transaction", {
      id: TRANSACTION_COLUMNS.OPTIONS,
      size,
      header: () => <HeadCellContent title="" />,
      enableSorting: false,
      cell: (info) => (
        <TransactionColumn.Actions
          onDuplicate={onDuplicate}
          info={info}
          onRowClick={onRowClick}
          sendToChat={(transactionIds) =>
            sendToChat({ transactionIds: [transactionIds] })
          }
          onRequestInfo={onRequestInfo}
        />
      ),
    }),
};

type TransactionTableBodyProps = {
  rows: Row<TransactionsType>[];
  onRowClick: (
    uuid: string,
    e: Event | React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void;
  isRowsSelected: boolean;
  actionIconColumn: string[];
  firstPinnedColumn: string | undefined;
  animateTransactions: string[];
  onAnimateComplete: () => void;
};

export const TransactionTableBody = ({
  rows,
  onRowClick,
  isRowsSelected,
  actionIconColumn,
  firstPinnedColumn,
  animateTransactions,
  onAnimateComplete,
}: TransactionTableBodyProps) => {
  return (
    <Table.Body>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <TransactionDraggableRow
            row={row}
            onClick={(e) => onRowClick(row.original.transaction.uuid, e)}
            className="t-group !t-border-none"
          >
            {row.getVisibleCells().map((cell) => {
              const styles = getCommonPinningStyles(cell.column);
              const isPinnedColumn = cell.column.getIsPinned();

              return (
                <Table.Cell
                  id={cell.id}
                  key={cell.id}
                  style={{
                    ...styles,
                    minWidth: cell.column.getSize(),
                    maxWidth: cell.column.getSize(),
                  }}
                  size={
                    actionIconColumn.includes(cell.column.id)
                      ? "small"
                      : "regular"
                  }
                  className={classNames(
                    "t-border-solid t-border-neutral-0 t-border-b t-border-0",
                    {
                      "group-hover:!t-bg-surface-lighter-grey": isPinnedColumn,
                      "t-drop-shadow-table-left-pin":
                        firstPinnedColumn === cell.column.id,
                      "!t-bg-purple--1": row.getIsSelected() && isPinnedColumn,
                      "group-hover:!t-opacity-100 !t-border-surface-transparent sm:!t-opacity-100":
                        cell.id ===
                        `${row.index}_${TRANSACTION_COLUMNS.SELECT}`,
                      "t-opacity-0":
                        cell.id ===
                          `${row.index}_${TRANSACTION_COLUMNS.SELECT}` &&
                        !isRowsSelected,
                      "!t-opacity-100": isRowsSelected,
                    }
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              );
            })}
          </TransactionDraggableRow>
          {animateTransactions.includes(row.original.transaction.uuid) && (
            <TransactionAnimateRow
              row={row}
              onAnimateComplete={onAnimateComplete}
            />
          )}
        </Fragment>
      ))}
    </Table.Body>
  );
};
